import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { AppState, Can, WantedItem } from '../../shared/api';
import { fitsWanted } from '../../shared/catalog';
import { ApiError, useSetWantedPreference, useUpdateProfile } from '../api';
import { wantedMeta } from '../format';
import { VariantPicker } from '../components/VariantPicker';
import { CanImage } from '../components/CanImage';
import { CanSearch } from '../components/CanSearch';
import { Sheet } from '../components/Sheet';

interface Props {
  state: AppState;
  /** Starts on the country and size the zoeklijst asks for. */
  onAdd: (want: WantedItem) => void;
  onWanted: (canId: number, wanted: boolean) => void;
  onRemove: (want: WantedItem) => Promise<void>;
  onUploadImage: (canId: number, file: File) => void;
  onNewFlavor: () => void;
  onReceive: (canId: number, friendName: string) => void;
  onPreview: () => void;
}

/** Below this many cans the list is short enough to read without a filter. */
const FILTER_FROM = 5;

/** The zoeklijst; sharing it with friends sits behind the Delen button. */
export function Wanted({
  state,
  onAdd,
  onWanted,
  onRemove,
  onUploadImage,
  onNewFlavor,
  onReceive,
  onPreview,
}: Props) {
  const owned = useMemo(() => new Set(state.entries.map((e) => e.canId)), [state.entries]);
  const wantedById = useMemo(() => new Map(state.wanted.map((w) => [w.canId, w])), [state.wanted]);
  const reservedBy = new Map(state.reservations.map((r) => [r.canId, r.friendName]));
  const canById = new Map(state.cans.map((c) => [c.id, c]));
  // Found means: on the wall in the country and size the line asks for.
  const missing = state.wanted
    .filter((w) => !state.entries.some((e) => fitsWanted(w, e)))
    .map((w) => canById.get(w.canId))
    .filter((c): c is Can => c !== undefined);
  // What a friend has for you is shown on top, not again in the list.
  const ready = missing.filter((c) => reservedBy.has(c.id));
  const open = missing.filter((c) => !reservedBy.has(c.id));
  const fileInput = useRef<HTMLInputElement>(null);
  const pickingFor = useRef<number | null>(null);
  const readyId = useId();
  const [sharing, setSharing] = useState(false);
  const [filter, setFilter] = useState('alle');
  const [choosing, setChoosing] = useState<WantedItem | null>(null);

  // The search offers what is not on the list yet.
  const searchable = useMemo(() => state.cans.filter((c) => !wantedById.has(c.id)), [state.cans, wantedById]);

  const allGroups = state.categories
    .map((cat) => ({ cat, items: open.filter((m) => m.categoryId === cat.id) }))
    .filter((g) => g.items.length > 0);
  // A filter on a category that just emptied falls back to everything.
  const activeFilter = allGroups.some((g) => g.cat.id === filter) ? filter : 'alle';
  const groups = allGroups.filter((g) => activeFilter === 'alle' || g.cat.id === activeFilter);

  const lede =
    missing.length === 0 ? (
      'Nog leeg. Zoek hieronder een blik dat je wilt hebben.'
    ) : (
      <>
        {open.length} nog te vinden
        {ready.length > 0 && (
          <>
            {' · '}
            <span className="lede__green">{ready.length} klaargezet</span>
          </>
        )}
      </>
    );

  return (
    <main className="screen screen--padded">
      <div className="wanted">
        <header className="wanted__head">
          <div>
            <h1 className="title">WANTED</h1>
            <div className="lede">{lede}</div>
          </div>
          <button type="button" className="pill share-pill" onClick={() => setSharing(true)}>
            <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 10V2M5 4.5 8 2l3 2.5M3 8v5h10V8" />
            </svg>
            Delen
          </button>
        </header>

        <div className="zoek-add">
          <CanSearch
            cans={searchable}
            categories={state.categories}
            limit={8}
            placeholder="Zet een blik op je lijst"
            label="Zoek een blik voor je zoeklijst"
            meta={(c) => (owned.has(c.id) ? '✓ in je collectie' : null)}
            onPick={(can) => onWanted(can.id, true)}
            notFound={
              <div className="pick-list__empty">
                Geen blik gevonden met die naam.{' '}
                <button type="button" className="text-link" onClick={onNewFlavor}>
                  Voeg de smaak toe
                </button>
              </div>
            }
          />
        </div>

        <div className="wanted__cols">
          {ready.length > 0 ? (
            <section className="ready" aria-labelledby={readyId}>
              <div className="ready__head">
                <h2 className="label label--green" id={readyId}>
                  Voor jou klaargezet
                </h2>
                <span>{ready.length}</span>
              </div>
              {ready.map((c) => {
                const friend = reservedBy.get(c.id) ?? '';
                return (
                  <div key={c.id} className="ready__row">
                    <div className="row__thumb">
                      <CanImage imageId={c.imageId} empty="blank" />
                    </div>
                    <div className="row__body">
                      <div className="row__name">{c.name}</div>
                      <div className="row__sub">
                        van {friend}
                        {wantedMeta(wantedById.get(c.id)!) && ` · ${wantedMeta(wantedById.get(c.id)!)}`}
                      </div>
                    </div>
                    <button type="button" className="receive-btn" onClick={() => onReceive(c.id, friend)}>
                      Ontvangen
                    </button>
                  </div>
                );
              })}
            </section>
          ) : (
            <section className="ready ready--empty" aria-labelledby={readyId}>
              <h2 className="label" id={readyId}>
                Voor jou klaargezet
              </h2>
              <p>Nog niemand heeft een blik voor je.</p>
              <button type="button" className="text-link" onClick={() => setSharing(true)}>
                Deel je zoeklijst
              </button>
            </section>
          )}

          <div className="wanted__list">
            {open.length >= FILTER_FROM && allGroups.length > 1 && (
              <div className="filter-row filter-row--wanted no-scrollbar" role="toolbar" aria-label="Filter op reeks">
                {[{ id: 'alle', name: 'Alles', count: open.length }, ...allGroups.map((g) => ({ ...g.cat, count: g.items.length }))].map(
                  (c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="chip filter-chip"
                      aria-pressed={activeFilter === c.id}
                      onClick={() => setFilter(c.id)}
                    >
                      {c.name}
                      <small>{c.count}</small>
                    </button>
                  ),
                )}
              </div>
            )}

            {groups.map(({ cat, items }) => (
              <section key={cat.id} className="group" aria-labelledby={`wanted-${cat.id}`}>
                <div className="cat-head">
                  <h2 className="cat-head__name" id={`wanted-${cat.id}`}>
                    {cat.name}
                  </h2>
                  <div className="cat-head__bar" />
                  <div className="cat-head__stat">{items.length}</div>
                </div>
                <div className="rows">
                  {items.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className="row row--button"
                      aria-label={`${m.name} bekijken`}
                      onClick={() => setChoosing(wantedById.get(m.id)!)}
                    >
                      <span className="row__thumb">
                        <CanImage imageId={m.imageId} empty="blank" />
                      </span>
                      <span className="row__body">
                        <span className="row__name">{m.name}</span>
                        <span className="row__sub">{wantedMeta(wantedById.get(m.id)!) ?? 'Elk land, elke inhoud'}</span>
                      </span>
                      <span className="row__chevron" aria-hidden="true">›</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}

            {open.length === 0 && (
              <div className="wanted__empty">
                <strong>{missing.length > 0 ? 'Alles is onderweg' : 'Je zoeklijst is leeg'}</strong>
                {missing.length > 0
                  ? 'Wat je nog zocht staat klaar bij je vrienden.'
                  : 'Zoek hierboven op naam, of scan een blik met de + in het menu.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {sharing && (
        <ShareSheet state={state} count={missing.length} onPreview={onPreview} onClose={() => setSharing(false)} />
      )}

      {choosing && canById.has(choosing.canId) && (
        <WantedDetail
          key={choosing.canId}
          can={canById.get(choosing.canId)!}
          category={state.categories.find((cat) => cat.id === canById.get(choosing.canId)?.categoryId)?.name}
          initialWant={choosing}
          want={wantedById.get(choosing.canId)}
          onAdd={onAdd}
          onRemove={onRemove}
          onPhoto={() => {
            pickingFor.current = choosing.canId;
            fileInput.current?.click();
          }}
          onClose={() => setChoosing((current) => current?.canId === choosing.canId ? null : current)}
        />
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && pickingFor.current !== null) onUploadImage(pickingFor.current, file);
          e.target.value = '';
        }}
      />
    </main>
  );
}

/** Keep the last preference while optimistic removal temporarily hides the line. */
function WantedDetail({ can, category, initialWant, want, onAdd, onRemove, onPhoto, onClose }: {
  can: Can;
  category?: string;
  initialWant: WantedItem;
  want?: WantedItem;
  onAdd: (want: WantedItem) => void;
  onRemove: (want: WantedItem) => Promise<void>;
  onPhoto: () => void;
  onClose: () => void;
}) {
  const latestWant = useRef(initialWant);
  if (want) latestWant.current = want;
  const current = latestWant.current;
  const addAfterClose = useRef<WantedItem | null>(null);
  const preference = useSetWantedPreference();
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = preference.isPending || removing;

  const setPreference = (item: WantedItem) => {
    setError(null);
    preference.mutate(item, { onError: (err) => setError(err.message) });
  };

  return (
    <Sheet label={`${can.name} · Zoeklijst`} onClose={() => {
      onClose();
      // Sheet has closed its native dialog before opening the collection sheet.
      if (addAfterClose.current) onAdd(addAfterClose.current);
    }}>
      {(close) => (
        <>
          <div className="sheet__top">
            <button type="button" className="sheet__img wanted-detail__photo" aria-label={`Foto van ${can.name} kiezen`} onClick={onPhoto} disabled={removing}>
              <CanImage imageId={can.imageId} />
              <span className="sheet__img-hint">Foto kiezen</span>
            </button>
            <div className="sheet__head">
              <div className="label label--green">{category} · Zoeklijst</div>
              <h2 className="sheet__name">{can.name}</h2>
            </div>
          </div>
          <div className="note">Zoek je een bepaald land of formaat? Zonder voorkeur telt elk blik van deze smaak.</div>
          <fieldset className="wanted-detail__preferences" disabled={busy} aria-label="Gezochte variant">
            <VariantPicker
              optional
              country={current.country}
              sizeMl={current.sizeMl}
              onCountry={(country) => setPreference({ ...current, country })}
              onSize={(sizeMl) => setPreference({ ...current, sizeMl })}
            />
          </fieldset>
          {error && <div className="error" role="alert">{error}</div>}
          {confirming ? (
            <>
              <div className="note">Weet je het zeker? {can.name} gaat van je zoeklijst.</div>
              <div className="sheet-actions wanted-detail__actions">
                <button type="button" className="btn btn--outline" disabled={removing} onClick={() => setConfirming(false)}>Annuleer</button>
                <button type="button" className="btn-done btn-done--danger" disabled={busy} onClick={async () => {
                  setRemoving(true);
                  setError(null);
                  try {
                    await onRemove(current);
                    close();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Verwijderen lukte niet. Probeer het opnieuw.');
                    setRemoving(false);
                  }
                }}>{removing ? 'Verwijderen…' : 'Van zoeklijst verwijderen'}</button>
              </div>
            </>
          ) : (
            <>
              <button type="button" className="btn wanted-detail__add" disabled={busy} onClick={() => {
                addAfterClose.current = latestWant.current;
                close();
              }}>Toevoegen aan collectie</button>
              <div className="sheet-actions wanted-detail__actions">
                <button type="button" className="btn-danger" disabled={busy} onClick={() => setConfirming(true)}>Van zoeklijst verwijderen</button>
                <button type="button" className="btn btn--outline" onClick={close}>Sluiten</button>
              </div>
            </>
          )}
        </>
      )}
    </Sheet>
  );
}

interface ShareProps {
  state: AppState;
  /** How many flavors a friend gets to see. */
  count: number;
  onPreview: () => void;
  onClose: () => void;
}

/** The link for friends, whether they may reserve, and the name and slug on it. */
export function ShareSheet({ state, count, onPreview, onClose }: ShareProps) {
  const { profile } = state;
  const updateProfile = useUpdateProfile();
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState(profile.displayName);
  const [slug, setSlug] = useState(profile.shareSlug);
  const [profileError, setProfileError] = useState<string | null>(null);
  const linkField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(profile.displayName);
    setSlug(profile.shareSlug);
  }, [profile.displayName, profile.shareSlug]);

  const shareUrl = `${location.origin}/${profile.shareSlug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // No clipboard access: leave the link selected to copy by hand.
      linkField.current?.select();
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const saveProfile = () => {
    setProfileError(null);
    updateProfile.mutate(
      { displayName: name.trim(), shareSlug: slug.trim().toLowerCase() },
      {
        onError: (err) => {
          setProfileError(err instanceof ApiError ? err.message : 'Opslaan lukte niet.');
          setName(profile.displayName);
          setSlug(profile.shareSlug);
        },
      },
    );
  };
  const profileDirty = name.trim() !== profile.displayName || slug.trim().toLowerCase() !== profile.shareSlug;

  return (
    <Sheet label="Deel je zoeklijst" onClose={onClose}>
      {(close) => (
        <>
          <h2 className="sheet__name">Deel je zoeklijst</h2>
          <div className="card__text">
            Iedereen met de link ziet welke {count} {count === 1 ? 'soort' : 'soorten'} je nog zoekt.
          </div>
          <div className="link-row">
            <input
              ref={linkField}
              className="link-field"
              readOnly
              aria-label="Je deellink"
              value={`${location.host}/${profile.shareSlug}`}
              onFocus={(e) => e.target.select()}
            />
            <button type="button" className="copy-btn" onClick={copy}>
              {copied ? 'Gekopieerd' : 'Kopieer'}
            </button>
          </div>
          <button
            type="button"
            className="toggle-row"
            role="switch"
            aria-checked={profile.allowReservations}
            onClick={() => updateProfile.mutate({ allowReservations: !profile.allowReservations })}
          >
            <div style={{ flex: 1 }}>
              <div className="toggle-row__title">Vrienden mogen reserveren</div>
              <div className="toggle-row__hint">
                {profile.allowReservations
                  ? 'Ze kunnen aangeven dat ze een blik voor je hebben.'
                  : 'Alleen-lezen: vrienden zien alleen de lijst.'}
              </div>
            </div>
            <div className="switch" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn--outline"
            style={{ marginTop: 16, width: '100%' }}
            onClick={() => {
              close();
              onPreview();
            }}
          >
            Bekijk als vriend
          </button>

          <details className="share-profile">
            <summary className="label">Naam en link aanpassen</summary>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (profileDirty) saveProfile();
              }}
            >
              <label className="field">
                <span className="field__label">Naam op je deellink</span>
                <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="field">
                <span className="field__label">Link</span>
                <input
                  className="input input--mono"
                  value={slug}
                  maxLength={40}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                />
              </label>
              {profileError && <div className="error">{profileError}</div>}
              {profileDirty && (
                <button type="submit" className="btn" style={{ marginTop: 14, height: 48, fontSize: 15 }}>
                  Opslaan
                </button>
              )}
            </form>
          </details>
        </>
      )}
    </Sheet>
  );
}
